import { isNil, Maybe, sleep } from "@instacover/utils"
import { captureException } from "@sentry/react"
import axios from "axios"

import { db, dexie } from "@/database/database"
import { useUploadProgressStore } from "@/services/store/uploadProgress"
import { useUploadUiStore } from "@/services/store/uploadUi"
import { trpc } from "@/utils/trpc"

import { logger } from "../utils/logging"

async function uploadFileToStorage(
  presignedPutUrl: string,
  file: File | Blob,
  onProgress?: (progress: number) => void,
) {
  await axios.put(presignedPutUrl, file, {
    headers: { "Content-Type": file.type },
    timeout: 5 * 60 * 1000,
    onUploadProgress: (event) => {
      const progress = Math.round((event.loaded * 100) / (event.total ?? 1))
      onProgress?.(progress)
    },
  })
}

async function process(sessionId: Maybe<string>) {
  if (isNil(sessionId) || sessionId === "") {
    logger.info("No sessionId found, skipping.")
    return
  }

  // user is offline
  if (!navigator.onLine) return

  const session = await db.session.get(sessionId)
  if (isNil(session)) {
    logger.info("Session not found, skipping.")
    return
  }

  // A retry is "picked up" the moment this cycle starts working, so the UI can move on
  // from the retry state to the regular upload progress.
  useUploadUiStore.getState().stopRetry()

  const slimPhotos = await db.photo.findAll({ sessionId, includeDeleted: true })

  for (const photo of slimPhotos) {
    if (photo.deleted) {
      //logger.debug(`Skipping photo ${photo.id} because it is deleted.`)
      continue
    }

    if (photo.uploadedAt) {
      //logger.debug(`Skipping photo ${photo.id} because it was already uploaded at ${photo.uploadedAt.toISOString()}.`)
      continue
    }

    if (photo.uploadFailed) {
      // Skip until user clicks retry — resetFailedUploads clears the flag.
      continue
    }

    const progressStore = useUploadProgressStore.getState()

    try {
      // photo with binary data
      const fullPhoto = await db.photo.findByIdFull(photo.id)

      if (fullPhoto?.data && fullPhoto.mime) {
        progressStore.setProgress(photo.id, 0)

        logger.info(`Fetching presigned url for photo ${photo.id}`)
        const { presignedUrl, storageRef } = await trpc.sessions.getPresignedUrl.mutate({
          contentType: fullPhoto.mime,
          sessionId,
          type: "photo",
        })

        logger.info(`Uploading photo ${photo.id} to bucket`, presignedUrl)
        const fileBuffer = new Uint8Array(fullPhoto.data)
        const blob = new Blob([fileBuffer], { type: fullPhoto.mime })
        await uploadFileToStorage(presignedUrl, blob, (p) => progressStore.setProgress(photo.id, p))

        logger.info(`Upserting photo ${photo.id} on server`)

        const upsertedPhoto = await trpc.sessions.upsertPhoto.mutate({
          id: photo.id,
          modelResult: photo.modelResult,
          storageRef,
          sessionId,
          latitude: photo.latitude,
          longitude: photo.longitude,
          accuracy: photo.accuracy,
          adjustedLatitude: photo.adjustedLatitude,
          adjustedLongitude: photo.adjustedLongitude,
          wallNumber: photo.wallNumber,
          type: photo.type,
          amenityId: photo.amenityId,
          buildingPolygonGeoJson: photo.buildingPolygonGeoJson,
          photoCoordinatesGeoJson: photo.photoCoordinatesGeoJson,
          visibleWallsGeoJson: photo.visibleWallsGeoJson,
        })

        logger.info(`Updating photo ${photo.id} on client`)
        await db.photo.update(photo.id, {
          url: upsertedPhoto.url,
          uploadedAt: new Date(),
          uploadFailed: false,
          uploadProgress: 101,
        })
      } else {
        logger.info(`Updating photo ${photo.id} on client`)
        await db.photo.update(photo.id, { uploadedAt: new Date(), uploadFailed: false, uploadProgress: 101 })
      }
    } catch (error) {
      logger.error(error, `Failed to process photo ${photo.id}, marking as failed.`)
      await db.photo.update(photo.id, { uploadFailed: true, uploadProgress: -1 })
    } finally {
      progressStore.clearProgress(photo.id)
    }
  }

  const slimDetails = await db.detail.findAll({ sessionId, includeDeleted: true })

  for (const detail of slimDetails) {
    if (detail.deleted) {
      //logger.debug(`Skipping detail ${detail.id} because it is deleted.`)
      continue
    }

    if (detail.uploadedAt && !detail.newInstahomePhotoId) {
      // logger.debug(
      //   `Skipping detail ${detail.id} because it was already uploaded at ${detail.uploadedAt.toISOString()}.`,
      // )
      continue
    }

    if (detail.uploadFailed) {
      // logger.debug(`Skipping detail ${detail.id} because it failed to upload.`)
      continue
    }

    // Uploading a detail before its main photo throws NOT_FOUND on the server.
    //
    // Both the check and the drop run in a single rw transaction. IndexedDB serializes
    // transactions with overlapping scopes, so a retake — which repoints details onto the newly
    // taken photo and deletes the old one — cannot commit between reading the parent and acting
    // on it. slimDetails is a snapshot from the start of this section and may already be stale,
    // hence the fresh read of the detail as well.
    const isReadyToUpload = await dexie.transaction("rw", [dexie.photo, dexie.detail], async (): Promise<boolean> => {
      const freshDetail = await db.detail.findById(detail.id)
      if (!freshDetail || freshDetail.deleted || freshDetail.uploadFailed) {
        return false
      }

      if (freshDetail.uploadedAt && !freshDetail.newInstahomePhotoId) {
        return false
      }

      const mainPhoto = await db.photo.findById(freshDetail.instahomePhotoId)

      // this should never happen as detail cannot exist without its main photo
      // otherwise user would be stuck at "uploading" state
      if (!mainPhoto || mainPhoto.deleted) {
        logger.warn(`Detail ${detail.id} has no usable main photo ${freshDetail.instahomePhotoId}, dropping it.`)
        captureException(new Error("Instahome detail without a usable main photo"), {
          extra: {
            detailId: detail.id,
            mainPhotoId: freshDetail.instahomePhotoId,
            mainPhotoWallNumber: mainPhoto?.wallNumber,
            sessionId,
          },
        })
        await db.detail.markAsDeleted(detail.id)
        return false
      }

      // Parent is still uploading or has failed
      return !!mainPhoto.uploadedAt
    })

    if (!isReadyToUpload) {
      continue
    }

    const detailProgressStore = useUploadProgressStore.getState()

    try {
      // main photo was re-taken
      if (detail.newInstahomePhotoId) {
        // detail is already on server -> just move it under new main photo
        if (detail.uploadedAt) {
          logger.info(`Moving detail ${detail.id} to a new main photo ${detail.newInstahomePhotoId}`)

          await trpc.sessions.moveDetailToNewPhoto.mutate({
            id: detail.id,
            newInstahomePhotoId: detail.newInstahomePhotoId,
            sessionId,
          })

          await db.detail.update(detail.id, { newInstahomePhotoId: undefined })
          continue
        } else {
          // not on the server yet, so drop the flag and let the upload below create it under the new parent
          await db.detail.update(detail.id, { newInstahomePhotoId: undefined })
        }
      }

      // Lazy-load binary data only for the detail being uploaded
      const fullDetail = await db.detail.findByIdFull(detail.id)

      if (fullDetail?.data && fullDetail.mime) {
        detailProgressStore.setProgress(detail.id, 0)

        logger.info(`Fetching presigned url for detail ${detail.id}`)
        const { presignedUrl, storageRef } = await trpc.sessions.getPresignedUrl.mutate({
          contentType: fullDetail.mime,
          sessionId,
          type: "detail",
        })

        logger.info(`Uploading detail ${detail.id} to bucket`)
        const fileBuffer = new Uint8Array(fullDetail.data)
        const blob = new Blob([fileBuffer], { type: fullDetail.mime })
        await uploadFileToStorage(presignedUrl, blob, (p) => detailProgressStore.setProgress(detail.id, p))

        logger.info(`Upserting detail ${detail.id} on server`)
        const upsertedDetail = await trpc.sessions.upsertPhotoDetail.mutate({
          id: detail.id,
          sessionId,
          storageRef,
          instahomePhotoId: detail.instahomePhotoId,
          accuracy: detail.accuracy,
          latitude: detail.latitude,
          longitude: detail.longitude,
          modelResult: detail.modelResult,
        })

        logger.info(`Updating detail ${detail.id} on client`)
        await db.detail.update(detail.id, {
          url: upsertedDetail.url,
          uploadedAt: new Date(),
          uploadFailed: false,
          uploadProgress: 101,
        })
      } else {
        logger.info(`Updating detail ${detail.id} on client`)
        await db.detail.update(detail.id, { uploadedAt: new Date(), uploadFailed: false, uploadProgress: 101 })
      }
    } catch (error) {
      logger.error(error, `Failed to process detail ${detail.id}, marking as failed.`)
      await db.detail.update(detail.id, { uploadFailed: true, uploadProgress: -1 })
    } finally {
      detailProgressStore.clearProgress(detail.id)
    }
  }

  // ==========================
  // deletions
  // ==========================
  for (const photo of slimPhotos) {
    if (!photo.deleted) {
      // logger.debug(`Skipping photo deletion ${photo.id} because it is not deleted.`)
      continue
    }

    if (!photo.uploadedAt) {
      // logger.debug(`Skipping photo deletion ${photo.id} because it hasn't been uploaded yet.`)
      continue
    }

    if (photo.deletedOnServer) {
      // logger.debug(`Skipping photo deletion ${photo.id} because it was already deleted on server.`)
      continue
    }

    try {
      logger.info(`Marking photo ${photo.id} as deleted on server.`)
      await trpc.sessions.deletePhoto.mutate({ id: photo.id })
      await db.photo.update(photo.id, { deletedOnServer: true })
    } catch (error) {
      logger.error(error, `Failed to mark photo ${photo.id} as deleted, aborting.`)
      return
    }
  }

  for (const detail of slimDetails) {
    if (!detail.deleted) {
      // logger.debug(`Skipping detail ${detail.id} because it is not deleted.`)
      continue
    }

    if (!detail.uploadedAt) {
      // logger.debug(`Skipping detail deletion ${detail.id} because it hasn't been uploaded yet.`)
      continue
    }

    if (detail.deletedOnServer) {
      // logger.debug(`Skipping detail deletion ${detail.id} because it was already deleted on server.`)
      continue
    }

    try {
      logger.info(`Marking detail ${detail.id} as deleted on server.`)
      await trpc.sessions.deleteDetail.mutate({ id: detail.id })
      await db.detail.update(detail.id, { deletedOnServer: true })
    } catch (error) {
      logger.error(error, `Failed to mark detail ${detail.id} as deleted, aborting.`)
      return
    }
  }
}

export async function startBackgroundUploader() {
  logger.debug("Starting background uploader")

  while (true) {
    // logger.debug(`Running on ${window.location.pathname}`)

    try {
      const [, sessionId] = window.location.pathname.split("/")
      await process(sessionId)
    } catch (error) {
      logger.error(error)
    }

    // logger.debug("Done, sleeping.")
    await sleep(5000)
  }
}
